import React, { useEffect, useRef } from 'react';

const Privacy = () => {
  const shieldRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.2 }
    );

    if (shieldRef.current) {
      observer.observe(shieldRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="w-full max-w-[1200px] px-4 py-20 md:px-10" id="privacy">
      <div className="flex flex-col lg:flex-row gap-12 items-center rounded-[3rem] bg-surface-dark p-8 md:p-12 border border-border-dark">
        <div className="flex-1 space-y-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-dark border border-border-dark text-primary text-xs font-bold mb-4 uppercase tracking-wider">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
              </svg>
              Security First
            </div>
            <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
              Your financial data stays with you.
            </h2>
            <p className="text-text-muted">We built UPI Trackr to be a fundamentally safe way to track your spending. That's why we're not a fintech app. We're fundamentally offline-first.</p>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-background-dark border border-border-dark">
              <div className="bg-primary/10 size-10 rounded-full flex items-center justify-center text-primary flex-shrink-0">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                </svg>
              </div>
              <span className="text-white text-sm font-medium">Encrypted local storage</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-background-dark border border-border-dark">
              <div className="bg-primary/10 size-10 rounded-full flex items-center justify-center text-primary flex-shrink-0">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                </svg>
              </div>
              <span className="text-white text-sm font-medium">PDF files processed on device</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-background-dark border border-border-dark">
              <div className="bg-primary/10 size-10 rounded-full flex items-center justify-center text-primary flex-shrink-0">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                </svg>
              </div>
              <span className="text-white text-sm font-medium">Google Drive private backup</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-background-dark border border-border-dark">
              <div className="bg-primary/10 size-10 rounded-full flex items-center justify-center text-primary flex-shrink-0">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              </div>
              <span className="text-white text-sm font-medium">No bank access</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-background-dark border border-border-dark">
              <div className="bg-primary/10 size-10 rounded-full flex items-center justify-center text-primary flex-shrink-0">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"/>
                </svg>
              </div>
              <span className="text-white text-sm font-medium">No data tracking</span>
            </div>
          </div>
        </div>
        <div className="flex-1 flex justify-center items-center">
          <div ref={shieldRef} className="relative size-80 md:size-96 rounded-full bg-background-dark border border-border-dark flex items-center justify-center opacity-0 scale-75 transition-all duration-700 ease-out">
            <div className="absolute inset-0 bg-primary/5 rounded-full animate-pulse"></div>
            <svg className="w-32 h-32 text-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              <path d="M12 12h7c-.53 4.12-3.28 7.79-7 8.94V12z" opacity="0.3"/>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Privacy;
